/**
 * RouteProgress — KERNEL "đừng bao giờ trắng trang khi đang tải".
 *
 * Bệnh (sếp Phú 25.08.2026): "cứ bấm card hay bị load giật trắng trang rồi lên popup".
 * Ba nguyên nhân chồng nhau, cái nào cũng cho ra một khoảng MÀN TRẮNG:
 *   1. Route lazy chưa tải → `<Suspense fallback={<div/>}>` thay TOÀN BỘ vùng nội dung bằng
 *      div rỗng. Core có 185 chỗ như vậy.
 *   2. Điều hướng KHÔNG phải transition → React bắt buộc phải hiện fallback (bỏ cây cũ) ngay
 *      lúc route mới suspend. Bật `future={{ v7_startTransition: true }}` cho BrowserRouter thì
 *      React GIỮ trang cũ tới khi chunk về — nhưng lúc đó người dùng bấm mà "không thấy gì",
 *      nên PHẢI có chỉ báo. Đó là việc của file này.
 *   3. Chunk popup (drawer) tải chậm với `fallback={null}` → bấm xong im lặng vài trăm ms
 *      rồi popup mới bung = "giật".
 *
 * ⚠️ BÀI HỌC ĐO ĐƯỢC (đừng viết lại thành state React): thanh tiến trình KHÔNG được là state
 * của cây React. Đã thử và đo trên harness: trong lúc transition treo chờ chunk, mọi update
 * đều phải render lại cây với location MỚI → lại suspend → React không commit → thanh không
 * bao giờ hiện, dù store đã `nav=true` và component đã subscribe. Nên thanh được dựng bằng
 * DOM THUẦN (appendChild vào body) ngoài vòng đời render; React chỉ còn nhiệm vụ báo "đã tới
 * nơi" qua effect theo location (effect đó chạy đúng lúc commit xong = đúng lúc cần tắt).
 *
 * Ba mảnh ghép, dùng chung cho Core · SaaS · Connect:
 *   - `<NavProgressBar/>`  : thanh 2px trên đỉnh. Mount MỘT lần trong AppLayout/Layout.
 *   - `<RouteFallback/>`   : fallback cho route lazy — khung xám giữ bố cục, KHÔNG trắng.
 *   - `<SuspenseBeacon/>`  : fallback vô hình cho popup — chỉ chạy thanh tiến trình.
 *
 * Thanh chỉ hiện sau 140ms: chunk đã prefetch về trong ~10ms mà nhấp nháy một cái thì còn
 * khó chịu hơn là không có gì.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SHOW_DELAY_MS = 140;
const GIVE_UP_MS = 10_000;
const BAR_ID = 'cnv-nav-progress';

// Store phẳng ở globalThis — cùng lý do như utils/openEntity: module có thể bị bundle 2 lần
// (host + @cnv/ui) và khi đó hai bản store riêng làm thanh tiến trình kẹt vĩnh viễn.
const STORE = (() => {
  const g = typeof globalThis !== 'undefined' ? globalThis : {};
  if (!g.__cnvNavProgress) {
    g.__cnvNavProgress = { nav: false, suspense: 0, patched: false, showTimer: null, giveUpTimer: null };
  }
  return g.__cnvNavProgress;
})();

/* ───────────────── Thanh tiến trình bằng DOM thuần ───────────────── */

function mountBar() {
  if (typeof document === 'undefined' || document.getElementById(BAR_ID)) return;
  const el = document.createElement('div');
  el.id = BAR_ID;
  el.setAttribute('role', 'presentation');
  el.setAttribute('aria-hidden', 'true');
  // z-index trên cả drawer/modal (z-[10001]) lẫn thanh v2. pointer-events:none để không
  // ăn mất cú bấm ở hàng trên cùng của trang.
  el.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'height:2px',
    'z-index:2147483000', 'pointer-events:none', 'overflow:hidden',
    'background:rgba(37,99,235,0.12)',
  ].join(';');
  const inner = document.createElement('div');
  inner.style.cssText = [
    'height:100%', 'width:45%',
    'background:linear-gradient(90deg, rgba(37,99,235,0) 0%, #2563eb 50%, rgba(37,99,235,0) 100%)',
    'animation:cnvNavBar 1.1s ease-in-out infinite',
  ].join(';');
  el.appendChild(inner);
  if (!document.getElementById('cnv-nav-progress-css')) {
    const style = document.createElement('style');
    style.id = 'cnv-nav-progress-css';
    style.textContent = '@keyframes cnvNavBar{0%{transform:translateX(-100%)}50%{transform:translateX(-15%)}100%{transform:translateX(100%)}}'
      + '@keyframes cnvFadeIn{from{opacity:0}to{opacity:1}}';
    document.head.appendChild(style);
  }
  document.body.appendChild(el);
}

function unmountBar() {
  const el = typeof document !== 'undefined' && document.getElementById(BAR_ID);
  if (el) el.remove();
}

/** Đồng bộ DOM theo store. Gọi được từ mọi nơi, kể cả ngoài React. */
function sync() {
  const active = STORE.nav || STORE.suspense > 0;
  if (active) {
    if (!STORE.showTimer && !document.getElementById(BAR_ID)) {
      STORE.showTimer = setTimeout(() => { STORE.showTimer = null; mountBar(); }, SHOW_DELAY_MS);
    }
    // Van an toàn: mất mạng giữa chừng thì thà tắt thanh còn hơn để nó chạy vĩnh viễn
    // như app treo.
    if (!STORE.giveUpTimer) {
      STORE.giveUpTimer = setTimeout(() => { STORE.giveUpTimer = null; STORE.nav = false; STORE.suspense = 0; sync(); }, GIVE_UP_MS);
    }
  } else {
    if (STORE.showTimer) { clearTimeout(STORE.showTimer); STORE.showTimer = null; }
    if (STORE.giveUpTimer) { clearTimeout(STORE.giveUpTimer); STORE.giveUpTimer = null; }
    unmountBar();
  }
}

/** Bắt đầu / kết thúc một pha "đang chờ chunk" — dùng cho popup, tab nội dung, mọi Suspense. */
export function beginPending() { STORE.suspense += 1; sync(); }
export function endPending() { STORE.suspense = Math.max(0, STORE.suspense - 1); sync(); }

/**
 * Vá history.pushState/replaceState để biết "người dùng VỪA bấm đi chỗ khác".
 *
 * Vì sao không dùng useLocation cho việc này: khi bật v7_startTransition, `useLocation()` chỉ
 * đổi lúc React COMMIT route mới — tức là SAU khi chunk tải xong. Đúng thứ ta cần đo là khoảng
 * TRƯỚC đó. `history.pushState` thì react-router gọi ngay lập tức, nên nó là mốc bắt đầu chính xác.
 *
 * Bắt cả đổi QUERY (không chỉ pathname): popup là URL-driven (`?taskId=`, `?dealId=`) nên mở
 * popup = đổi query. Lọc bằng "chỉ pathname" thì đúng cái ca sếp báo lại không có chỉ báo nào.
 * Sợ thanh chạy loạn khi lọc bảng là thừa: nó chỉ hiện khi chờ quá 140ms, mà đổi query không
 * kèm chunk mới thì commit gần như tức thì.
 */
function patchHistory() {
  if (STORE.patched || typeof window === 'undefined' || !window.history) return;
  STORE.patched = true;
  const url = () => window.location.pathname + window.location.search;
  for (const name of ['pushState', 'replaceState']) {
    const orig = window.history[name];
    if (typeof orig !== 'function') continue;
    window.history[name] = function patched(...args) {
      const before = url();
      const out = orig.apply(this, args);
      if (url() !== before && !STORE.nav) { STORE.nav = true; sync(); }
      return out;
    };
  }
  // Nút Back/Forward cũng là điều hướng (không đi qua pushState).
  window.addEventListener('popstate', () => { if (!STORE.nav) { STORE.nav = true; sync(); } });
}

/**
 * Mount MỘT lần trong layout. Bản thân component không render gì — thanh là DOM thuần
 * (xem ghi chú đầu file); component chỉ giữ hai việc mà chỉ React biết:
 * vá history lúc app khởi động, và "route mới đã commit" để tắt thanh.
 */
export function NavProgressBar() {
  const location = useLocation();

  useEffect(() => { patchHistory(); return () => { STORE.nav = false; sync(); }; }, []);

  useEffect(() => {
    if (STORE.nav) { STORE.nav = false; sync(); }
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Fallback VÔ HÌNH: không chiếm chỗ, chỉ báo cho thanh tiến trình biết "đang chờ chunk".
 * Dùng cho popup/drawer — thứ mở ĐÈ lên trang, không được che nội dung đang xem.
 */
export function SuspenseBeacon() {
  useEffect(() => { beginPending(); return endPending; }, []);
  return null;
}

/**
 * Fallback cho ROUTE lazy: giữ bố cục bằng khung xám mờ thay vì bỏ trắng.
 * Hiện sau 150ms (animation-delay, không cần state) để chunk nhanh không gây chớp.
 */
export function RouteFallback() {
  useEffect(() => { beginPending(); return endPending; }, []);
  return (
    <div
      aria-busy="true"
      className="animate-pulse"
      style={{ animation: 'cnvFadeIn .18s ease .15s both' }}
    >
      <style>{'@keyframes cnvFadeIn{from{opacity:0}to{opacity:1}}'}</style>
      <div className="h-7 w-52 rounded-lg bg-gray-200/70 mb-5" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="h-20 rounded-xl bg-gray-200/60" />
        <div className="h-20 rounded-xl bg-gray-200/60" />
        <div className="h-20 rounded-xl bg-gray-200/60" />
      </div>
      <div className="rounded-xl bg-gray-200/50 h-64" />
    </div>
  );
}

export default NavProgressBar;
